import { parsePhoneNumberFromString, CountryCode } from 'libphonenumber-js';

export interface PhoneValidationResponse {
	success: boolean;
	message: string;
	formattedPhoneNumber?: string;
}

export interface LocationContext {
	city?: string;
	state?: string;
	postalCode?: string;
}

// Country name to ISO code mapping for common countries
const COUNTRY_NAME_TO_CODE: Record<string, string> = {
	'united states': 'US',
	usa: 'US',
	america: 'US',
	canada: 'CA',
	mexico: 'MX',
	argentina: 'AR',
	brazil: 'BR',
	chile: 'CL',
	colombia: 'CO',
	peru: 'PE',
	venezuela: 'VE',
	ecuador: 'EC',
	bolivia: 'BO',
	paraguay: 'PY',
	uruguay: 'UY',
	'united kingdom': 'GB',
	uk: 'GB',
	'great britain': 'GB',
	england: 'GB',
	scotland: 'GB',
	wales: 'GB',
	ireland: 'IE',
	france: 'FR',
	germany: 'DE',
	italy: 'IT',
	spain: 'ES',
	portugal: 'PT',
	netherlands: 'NL',
	belgium: 'BE',
	switzerland: 'CH',
	austria: 'AT',
	sweden: 'SE',
	norway: 'NO',
	denmark: 'DK',
	finland: 'FI',
	poland: 'PL',
	russia: 'RU',
	ukraine: 'UA',
	'czech republic': 'CZ',
	hungary: 'HU',
	romania: 'RO',
	bulgaria: 'BG',
	greece: 'GR',
	turkey: 'TR',
	israel: 'IL',
	japan: 'JP',
	'south korea': 'KR',
	korea: 'KR',
	china: 'CN',
	taiwan: 'TW',
	'hong kong': 'HK',
	singapore: 'SG',
	malaysia: 'MY',
	thailand: 'TH',
	philippines: 'PH',
	indonesia: 'ID',
	vietnam: 'VN',
	india: 'IN',
	pakistan: 'PK',
	bangladesh: 'BD',
	australia: 'AU',
	'new zealand': 'NZ',
	'south africa': 'ZA',
	egypt: 'EG',
	nigeria: 'NG',
	kenya: 'KE',
	morocco: 'MA',
	algeria: 'DZ',
	tunisia: 'TN',
	libya: 'LY',
	sudan: 'SD',
	ethiopia: 'ET',
	ghana: 'GH',
	uganda: 'UG',
	tanzania: 'TZ',
	zimbabwe: 'ZW',
	botswana: 'BW',
	namibia: 'NA',
	zambia: 'ZM',
	malawi: 'MW',
	mozambique: 'MZ',
	madagascar: 'MG',
	mauritius: 'MU',
	seychelles: 'SC',
	reunion: 'RE',
	mayotte: 'YT',
	comoros: 'KM',
};

// Helper function to convert country name to ISO code
const convertCountryNameToCode = (countryName: string): string => {
	const normalizedName = countryName.toLowerCase().trim();
	return COUNTRY_NAME_TO_CODE[normalizedName] || 'US'; // Default to US if not found
};

/**
 * Validates a phone number using libphonenumber-js with extracted context information
 * @param phone_number - The phone number to validate
 * @param countryName - Country name (e.g., "United States", "Argentina")
 * @param locationContext - Optional location context for better error messages
 * @returns PhoneValidationResponse with validation result and formatted number
 */
export const validatePhoneNumber = async (
	phone_number: string,
	countryName?: string,
	locationContext?: LocationContext,
): Promise<PhoneValidationResponse> => {
	try {
		if (!phone_number || phone_number.trim() === '') {
			return {
				success: false,
				message: 'Phone number is required.',
			};
		}

		const cleanPhoneNumber = phone_number.trim();

		// Special case: If phone number starts with +, validate as international number only
		if (cleanPhoneNumber.startsWith('+')) {
			try {
				const phoneNumber = parsePhoneNumberFromString(cleanPhoneNumber);

				if (phoneNumber && phoneNumber.isValid()) {
					return {
						success: true,
						message: 'Phone number is valid.',
						formattedPhoneNumber: phoneNumber.formatInternational(),
					};
				} else {
					return {
						success: false,
						message:
							'The international phone number format appears to be invalid. Please check the number and try again.',
					};
				}
			} catch (parseError) {
				return {
					success: false,
					message:
						'The international phone number format appears to be invalid. Please check the number and try again.',
				};
			}
		}

		// Extract country code from country name for non-international numbers
		let countryCode: CountryCode | undefined;
		let phoneNumberToValidate = cleanPhoneNumber;

		if (countryName) {
			countryCode = convertCountryNameToCode(countryName) as CountryCode;
		} else {
			// If no country is provided, add +1 prefix (US) to the phone number
			if (!cleanPhoneNumber.startsWith('+')) {
				phoneNumberToValidate = '+1' + cleanPhoneNumber;
			}
		}

		try {
			let phoneNumber;
			const parseAttempts = [];

			// Strategy 1: Try with country code first if available (handles national numbers)
			if (countryCode) {
				try {
					phoneNumber = parsePhoneNumberFromString(
						phoneNumberToValidate,
						countryCode,
					);
					parseAttempts.push({
						method: `with country code ${countryCode}`,
						success: true,
						result: phoneNumber ? phoneNumber.number : null,
					});
				} catch (parseError) {
					const errorMessage =
						parseError instanceof Error
							? parseError.message
							: String(parseError);
					parseAttempts.push({
						method: `with country code ${countryCode}`,
						success: false,
						error: errorMessage,
					});
					console.warn(
						'⚠️ [PHONE VALIDATION] Parsing with country code failed:',
						errorMessage,
					);
				}
			}

			// Strategy 2: Try without country code (for international format numbers like +54...)
			if (!phoneNumber) {
				try {
					phoneNumber = parsePhoneNumberFromString(phoneNumberToValidate);
					parseAttempts.push({
						method: 'without country code (international)',
						success: true,
						result: phoneNumber ? phoneNumber.number : null,
					});
				} catch (parseError) {
					const errorMessage =
						parseError instanceof Error
							? parseError.message
							: String(parseError);
					parseAttempts.push({
						method: 'without country code (international)',
						success: false,
						error: errorMessage,
					});
					console.warn(
						'⚠️ [PHONE VALIDATION] International format parsing failed:',
						errorMessage,
					);
				}
			}

			// Strategy 3: If still no success and we have country, try with '+' prefix
			if (!phoneNumber && countryCode && !phoneNumberToValidate.startsWith('+')) {
				try {
					const withPlusPrefix = '+' + phoneNumberToValidate;
					phoneNumber = parsePhoneNumberFromString(withPlusPrefix, countryCode);
					parseAttempts.push({
						method: `auto-corrected with + prefix and ${countryCode}`,
						success: true,
						result: phoneNumber ? phoneNumber.number : null,
					});
				} catch (parseError) {
					const errorMessage =
						parseError instanceof Error
							? parseError.message
							: String(parseError);
					parseAttempts.push({
						method: `auto-corrected with + prefix and ${countryCode}`,
						success: false,
						error: errorMessage,
					});
					console.warn(
						'⚠️ [PHONE VALIDATION] Auto-correction failed:',
						errorMessage,
					);
				}
			}

			// If all strategies failed
			if (!phoneNumber) {
				console.error(
					'❌ [PHONE VALIDATION] All parsing strategies failed:',
					parseAttempts,
				);

				// Provide more specific error message based on the context
				let errorMessage =
					'The phone number format appears to be invalid. Please provide a valid phone number.';

				// Check if this looks like a fixed line number without area code
				if (phoneNumberToValidate.length >= 7 && phoneNumberToValidate.length <= 9) {
					const locationInfo =
						locationContext?.city && locationContext?.state
							? ` for ${locationContext.city}, ${locationContext.state}`
							: locationContext?.city
								? ` for ${locationContext.city}`
								: '';

					errorMessage = `This appears to be a fixed line number but is missing the area code${locationInfo}. Please include the area code or provide the complete number with area code.`;
				} else {
					errorMessage =
						'The phone number format appears to be invalid. Please provide a valid phone number.';
				}

				return {
					success: false,
					message: errorMessage,
				};
			}

			// Check if the parsed number is valid
			if (phoneNumber) {
				const isValid = phoneNumber.isValid();

				if (isValid) {
					// Always format with international format to include country code
					const formattedPhoneNumber = phoneNumber.formatInternational();

					return {
						success: true,
						message: 'Phone number is valid.',
						formattedPhoneNumber,
					};
				} else {
					return {
						success: false,
						message:
							'The phone number format appears to be invalid. Please provide a valid phone number.',
					};
				}
			} else {
				console.error('❌ [PHONE VALIDATION] No phone number parsed');
				return {
					success: false,
					message:
						'The phone number format appears to be invalid. Please provide a valid phone number.',
				};
			}
		} catch (parseError) {
			console.error(
				'💥 [PHONE VALIDATION] Unexpected error during validation:',
				parseError,
			);
			return {
				success: false,
				message:
					'The phone number format appears to be invalid. Please provide a valid phone number.',
			};
		}
	} catch (err: any) {
		console.error('💥 [PHONE VALIDATION] Unexpected top-level error:', err);
		return {
			success: false,
			message:
				'The phone number format appears to be invalid. Please provide a valid phone number.',
		};
	}
};
